import { useEffect, useState } from 'react';
import {
    CpmButton,
    CpmModal,
    CpmModalBody,
    CpmModalFooter,
} from '../../../../components/design-system';
import { deleteMaster } from '../../../../services/productMasterService';
import { getApiErrorMessage } from '../../../../utils/api/ApiClient';
import { toast } from '../../../../utils/toast';

interface DeleteConfirmDialogProps {
    open: boolean;
    level: string;
    id: string;
    name: string;
    onClose: () => void;
    onDeleted: () => void;
}

type Phase = 'confirm' | 'deleting' | 'blocked' | 'saveError';

export default function DeleteConfirmDialog({
    open,
    level,
    id,
    name,
    onClose,
    onDeleted,
}: DeleteConfirmDialogProps) {
    const [phase, setPhase] = useState<Phase>('confirm');
    const [blocker, setBlocker] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setPhase('confirm');
            setBlocker(null);
            setSaveError(null);
        }
    }, [open, id]);

    const levelLabel =
        level === 'brand'
            ? 'brand'
            : level === 'product'
              ? 'product'
              : level === 'variant'
                ? 'variant'
                : 'state product';

    const confirm = async () => {
        try {
            setPhase('deleting');
            setBlocker(null);
            setSaveError(null);
            await deleteMaster({ level, id });
            toast.success('Record deleted');
            onDeleted();
            onClose();
        } catch (err) {
            const msg = getApiErrorMessage(err);
            if (msg.toLowerCase().includes('cannot delete')) {
                setBlocker(msg);
                setPhase('blocked');
            } else {
                setSaveError(msg || 'The delete was rejected');
                setPhase('saveError');
            }
        }
    };

    const busy = phase === 'deleting';

    return (
        <CpmModal open={open} onClose={busy ? () => undefined : onClose} maxWidth={540} zIndex={70}>
            <div className="flex items-start gap-[14px]" style={{ padding: '22px 24px 16px' }}>
                <span
                    className={[
                        'shrink-0 inline-flex items-center justify-center w-[38px] h-[38px] rounded-[10px]',
                        phase === 'blocked' || phase === 'saveError'
                            ? 'bg-[var(--cpm-warning-bg)] text-[var(--cpm-warning-text)]'
                            : 'bg-[var(--cpm-shortfall-bg)] text-[var(--cpm-shortfall-text)]',
                    ].join(' ')}
                >
                    {phase === 'blocked' || phase === 'saveError' ? (
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <path d="M3 6h18" strokeLinecap="round" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" strokeLinecap="round" />
                        </svg>
                    )}
                </span>
                <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                    <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                        {phase === 'blocked'
                            ? `Can't delete this ${levelLabel}`
                            : phase === 'saveError'
                              ? 'The delete was rejected'
                              : `Delete this ${levelLabel}?`}
                    </h2>
                    <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        {phase === 'blocked'
                            ? `“${name}” still has attachments that block hard delete.`
                            : phase === 'saveError'
                              ? saveError ||
                                'The catalog service did not accept the delete. Nothing was removed.'
                              : `“${name}” will be permanently removed from the master catalog.`}
                    </p>
                </div>
            </div>

            <CpmModalBody className="!pt-0">
                {phase === 'blocked' && blocker ? (
                    <div className="flex flex-col" style={{ gap: 8 }}>
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Still attached
                        </span>
                        <div
                            className="flex items-center rounded-[10px] bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)]"
                            style={{ gap: 12, padding: '11px 14px' }}
                        >
                            <span className="flex-1 !text-[12.5px] !font-bold !text-[var(--cpm-warning-text)]">
                                {blocker}
                            </span>
                        </div>
                    </div>
                ) : null}
            </CpmModalBody>

            <CpmModalFooter>
                <div className="ml-auto flex items-center gap-[10px]">
                    <CpmButton
                        variant="outline"
                        size="modal"
                        onClick={onClose}
                        disabled={busy}
                    >
                        {phase === 'blocked' ? 'Close' : phase === 'saveError' ? 'Close' : 'Cancel'}
                    </CpmButton>
                    {phase === 'blocked' ? null : (
                        <CpmButton
                            variant="danger"
                            size="modal"
                            onClick={confirm}
                            isLoading={busy}
                        >
                            {busy
                                ? 'Deleting…'
                                : phase === 'saveError'
                                  ? 'Try again'
                                  : 'Delete record'}
                        </CpmButton>
                    )}
                </div>
            </CpmModalFooter>
        </CpmModal>
    );
}
